import { Link } from 'react-router-dom';
import { X, Bug, Lightbulb, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

interface FeedbackPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackPopup = ({ isOpen, onClose }: FeedbackPopupProps) => {

  if (!isOpen) return null;

  const feedbackOptions = [
    {
      type: 'bug',
      icon: Bug,
      title: 'Found a Bug',
      description: 'Something not working as expected? Report it so we can fix it.',
      color: 'text-brand-red',
      bgColor: 'bg-brand-red/10 dark:bg-brand-red/20',
      link: '/feedback?type=bug'
    },
    {
      type: 'feature',
      icon: Lightbulb,
      title: 'Feature Request',
      description: 'Have an idea for a new feature? We\'d love to hear it!',
      color: 'text-brand-gold',
      bgColor: 'bg-brand-gold/10 dark:bg-brand-gold/20',
      link: '/feedback?type=feature'
    }
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40 bg-black/20" 
        onClick={onClose}
      />
      
      {/* Popup */}
      <div className="fixed bottom-6 right-6 z-50">
        <div className="bg-white dark:bg-brand-dark rounded-lg shadow-xl w-96 transform transition-all duration-300 ease-out animate-in slide-in-from-bottom-2 fade-in-0 border border-brand-border">
          <div className="flex items-center justify-between p-6 border-b border-brand-border">
            <h2 className="text-lg font-semibold text-foreground">
              How can we help?
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="p-6 space-y-4">
            {feedbackOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <Link
                  key={option.type}
                  to={option.link}
                  onClick={onClose}
                  className={`block p-4 rounded-lg border border-brand-border hover:border-brand-light hover:bg-brand-light/10 hover:shadow-lg hover:shadow-brand-light/20 hover:scale-[1.02] transition-all duration-200 group ${option.bgColor}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg group-hover:scale-110 transition-transform duration-200 ${option.bgColor}`}>
                        <IconComponent className={`h-5 w-5 ${option.color} group-hover:scale-110 transition-transform duration-200`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-foreground">
                          {option.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {option.description}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-all duration-200" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default FeedbackPopup;
